// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { avoidBreakInsidePrintElement, styledScrollbar, xScreen } from "@sc/XDL/Utilities";
import color from "@sc/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@sc/XDL/Utilities/links";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import styled, { css } from "styled-components";

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;

        @media print {
            display: block;
            padding: 0;
            margin: 0 15px;
            width: 100%;
        }
    `,
};

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    linkStyles?: LinkPresentationProps,
    typographyStyles?: TypographyPresentationProps,
    iconStyles?: IconPresentationProps,
    iconPosition: "left" | "right" = "left",
) => {
    const marginPosition = (linkStyles?.icon?.position || iconPosition) === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: mergeWithCss(
                    {
                        size: 12,
                        lineHeight: "14px",
                        weight: "bold",
                        css: css`
                            letter-spacing: 0;
                        `,
                    },
                    typographyStyles,
                ),
                icon: {
                    iconProps: mergeWithCss(
                        {
                            src: icon,
                            size: 13,
                            css: css`
                                ${marginPosition}: 8px;
                                opacity: 0.66;
                            `,
                        },
                        iconStyles,
                    ),
                    position: linkStyles?.icon?.position || iconPosition,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        line-height: 12px;
                    `,
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                    display: flex;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

export const dotBttonStyles: ButtonPresentationProps = {
    css: css`
        height: 10px;
        width: 10px;
        min-height: unset;
        padding: 0;
        margin-right: 4px;
        border: 1px solid ${color("common.white")};
        border-radius: 5px;
        background: transparent;

        &:last-child {
            margin-right: 0;
        }

        ${xScreen("lte", "md")} {
            border-color: ${color("common.ui4")};
        }

        &:active,
        &:focus {
            &&&& {
                width: 20px;
                background: ${color("common.white")};
                border-color: ${color("common.white")};
                outline: none;

                ${xScreen("lte", "md")} {
                    background: ${color("common.ui9")};
                    border-color: ${color("common.ui9")};
                }
            }
        }
        &:hover {
            &&&& {
                background: ${color("common.ui8")};
                border-color: ${color("common.white")};
                outline: none;
                ${xScreen("lte", "md")} {
                    background: ${color("common.ui9")};
                    border-color: ${color("common.ui9")};
                }
            }
        }

        &.slide-dot-button-selected {
            width: 20px;
            background: ${color("common.white")};
            ${xScreen("lte", "md")} {
                background: ${color("common.ui9")};
                border-color: ${color("common.ui9")};
            }
        }
    `,
};

export const StyledHr = getStyledWrapper("hr");
